import { stockService } from './stockService';
import {
  sendLocalNotification,
  getNotificationPermission,
  getNotificationPreferences,
  saveNotificationPreferences,
  NotificationPreferences,
} from './notificationService';
import type { Stock } from '@/types/inventory.types';

export interface Alert {
  id: string;
  type: 'critical' | 'warning';
  productId: string;
  productName: string;
  category: string;
  currentStock: number;
  threshold: number;
  unit: string;
  zone: string;
  createdAt: Date;
  read: boolean;
  notified: boolean;
}

export interface AlertStats {
  critical: number;
  warning: number;
  total: number;
  unread: number;
}

const ALERTS_STORAGE_KEY = 'stock_alerts';
const LAST_CHECK_KEY = 'last_alert_check';
const CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

class AlertService {
  private checkInterval: number | null = null;
  private listeners: Set<(alerts: Alert[]) => void> = new Set();

  /**
   * Initialiser le service d'alertes
   */
  initialize(): void {
    // Vérifier au démarrage après un délai
    setTimeout(() => {
      this.checkIfNeeded();
    }, 3000);

    // Configurer la vérification périodique
    this.startPeriodicCheck();

    // Écouter les changements de visibilité de la page
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.checkIfNeeded();
      }
    });
  }

  /**
   * Démarrer la vérification périodique
   */
  private startPeriodicCheck(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
    this.checkInterval = window.setInterval(() => {
      this.checkIfNeeded();
    }, CHECK_INTERVAL);
  }

  /**
   * Vérifier si une vérification est nécessaire (basé sur le dernier check)
   */
  private checkIfNeeded(): void {
    const lastCheck = localStorage.getItem(LAST_CHECK_KEY);
    const now = Date.now();

    if (!lastCheck || now - parseInt(lastCheck, 10) > CHECK_INTERVAL) {
      // Ne pas bloquer si pas de restaurant ID disponible
      const restaurantId = this.getCurrentRestaurantId();
      if (restaurantId) {
        this.checkStockAlerts(restaurantId);
      }
    }
  }

  /**
   * Obtenir l'ID du restaurant actuel (depuis localStorage ou autre source)
   */
  private getCurrentRestaurantId(): string | null {
    try {
      const authData = localStorage.getItem('auth_state');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.currentRestaurantId || null;
      }
    } catch {
      // Ignorer les erreurs de parsing
    }
    return null;
  }

  /**
   * Vérifier les stocks et créer des alertes si nécessaire
   */
  async checkStockAlerts(restaurantId: string): Promise<Alert[]> {
    try {
      localStorage.setItem(LAST_CHECK_KEY, Date.now().toString());

      const [criticalResult, warningResult, allStocksResult] = await Promise.all([
        stockService.getCriticalStocks(restaurantId),
        stockService.getWarningStocks(restaurantId),
        stockService.getStocks(restaurantId),
      ]);

      // Calculer le stock total par produit
      const stockTotalByProduct: Record<string, number> = {};
      if (allStocksResult.data) {
        allStocksResult.data.forEach((stock) => {
          stockTotalByProduct[stock.produit_id] =
            (stockTotalByProduct[stock.produit_id] || 0) + stock.quantite;
        });
      }

      const existingAlerts = this.getAlerts();
      const existingAlertIds = new Set(existingAlerts.map(a => `${a.productId}-${a.type}`));
      const newAlerts: Alert[] = [];

      // Créer des alertes pour les stocks critiques
      const criticalStocks = criticalResult.data || [];
      for (const stock of criticalStocks) {
        const alertId = `${stock.produit_id}-critical`;
        if (!existingAlertIds.has(alertId)) {
          newAlerts.push(this.createAlert(stock, 'critical', stockTotalByProduct[stock.produit_id]));
        }
      }

      // Créer des alertes pour les stocks en warning
      const warningStocks = warningResult.data || [];
      for (const stock of warningStocks) {
        const alertId = `${stock.produit_id}-warning`;
        if (!existingAlertIds.has(alertId)) {
          newAlerts.push(this.createAlert(stock, 'warning', stockTotalByProduct[stock.produit_id]));
        }
      }

      // Supprimer les alertes obsolètes (stock rétabli)
      const currentProductIds = new Set([
        ...criticalStocks.map(s => `${s.produit_id}-critical`),
        ...warningStocks.map(s => `${s.produit_id}-warning`),
      ]);

      const updatedAlerts = existingAlerts.filter(alert => {
        const alertKey = `${alert.productId}-${alert.type}`;
        return currentProductIds.has(alertKey);
      });

      // Ajouter les nouvelles alertes
      const allAlerts = [...updatedAlerts, ...newAlerts];
      this.saveAlerts(allAlerts);

      // Envoyer des notifications pour les nouvelles alertes
      if (newAlerts.length > 0) {
        await this.sendNotifications(newAlerts);
      }

      // Notifier les listeners
      this.notifyListeners(allAlerts);

      return allAlerts;
    } catch (error) {
      console.error('Erreur vérification alertes:', error);
      return this.getAlerts();
    }
  }

  /**
   * Créer une alerte à partir d'un stock
   */
  private createAlert(stock: Stock, type: 'critical' | 'warning', totalStock?: number): Alert {
    return {
      id: `${stock.produit_id}-${type}-${Date.now()}`,
      type,
      productId: stock.produit_id,
      productName: stock.produits?.nom || 'Produit inconnu',
      category: stock.produits?.categorie || 'Autres',
      currentStock: totalStock ?? stock.quantite,
      threshold: type === 'critical'
        ? stock.produits?.seuil_critique || 0
        : stock.produits?.seuil_alerte || 0,
      unit: stock.produits?.unite || 'unité',
      zone: stock.zone,
      createdAt: new Date(),
      read: false,
      notified: false,
    };
  }

  /**
   * Envoyer des notifications pour les nouvelles alertes
   */
  private async sendNotifications(alerts: Alert[]): Promise<void> {
    const prefs = getNotificationPreferences();
    const permission = getNotificationPermission();

    if (!prefs.criticalStock || permission !== 'granted') {
      return;
    }

    const criticalAlerts = alerts.filter(a => a.type === 'critical' && !a.notified);

    if (criticalAlerts.length > 0) {
      const title = criticalAlerts.length === 1
        ? `Stock critique: ${criticalAlerts[0].productName}`
        : `${criticalAlerts.length} produits en rupture`;

      const body = criticalAlerts.length === 1
        ? `${criticalAlerts[0].currentStock} ${criticalAlerts[0].unit} restant(s)`
        : criticalAlerts.map(a => a.productName).join(', ');

      await sendLocalNotification(title, {
        body,
        tag: 'critical-stock',
        requireInteraction: true,
        data: {
          type: 'critical_stock',
          alertIds: criticalAlerts.map(a => a.id),
        },
      });

      // Marquer comme notifié
      const updatedAlerts = this.getAlerts().map(alert => {
        if (criticalAlerts.some(ca => ca.id === alert.id)) {
          return { ...alert, notified: true };
        }
        return alert;
      });
      this.saveAlerts(updatedAlerts);
    }
  }

  /**
   * Récupérer toutes les alertes
   */
  getAlerts(): Alert[] {
    try {
      const stored = localStorage.getItem(ALERTS_STORAGE_KEY);
      if (stored) {
        const alerts = JSON.parse(stored);
        return alerts.map((a: any) => ({
          ...a,
          createdAt: new Date(a.createdAt),
        }));
      }
    } catch (error) {
      console.error('Erreur lecture alertes:', error);
    }
    return [];
  }

  /**
   * Sauvegarder les alertes
   */
  private saveAlerts(alerts: Alert[]): void {
    try {
      localStorage.setItem(ALERTS_STORAGE_KEY, JSON.stringify(alerts));
    } catch (error) {
      console.error('Erreur sauvegarde alertes:', error);
    }
  }

  /**
   * Obtenir les statistiques des alertes
   */
  getAlertStats(): AlertStats {
    const alerts = this.getAlerts();
    return {
      critical: alerts.filter(a => a.type === 'critical').length,
      warning: alerts.filter(a => a.type === 'warning').length,
      total: alerts.length,
      unread: alerts.filter(a => !a.read).length,
    };
  }

  /**
   * Marquer une alerte comme lue
   */
  markAsRead(alertId: string): void {
    const alerts = this.getAlerts().map(alert => {
      if (alert.id === alertId) {
        return { ...alert, read: true };
      }
      return alert;
    });
    this.saveAlerts(alerts);
    this.notifyListeners(alerts);
  }

  /**
   * Marquer toutes les alertes comme lues
   */
  markAllAsRead(): void {
    const alerts = this.getAlerts().map(alert => ({ ...alert, read: true }));
    this.saveAlerts(alerts);
    this.notifyListeners(alerts);
  }

  /**
   * Supprimer une alerte
   */
  dismissAlert(alertId: string): void {
    const alerts = this.getAlerts().filter(alert => alert.id !== alertId);
    this.saveAlerts(alerts);
    this.notifyListeners(alerts);
  }

  /**
   * Supprimer toutes les alertes
   */
  clearAlerts(): void {
    this.saveAlerts([]);
    this.notifyListeners([]);
  }

  /**
   * S'abonner aux changements d'alertes
   */
  subscribe(listener: (alerts: Alert[]) => void): () => void {
    this.listeners.add(listener);
    // Envoyer l'état actuel immédiatement
    listener(this.getAlerts());

    // Retourner la fonction de désabonnement
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notifier tous les listeners
   */
  private notifyListeners(alerts: Alert[]): void {
    this.listeners.forEach(listener => listener(alerts));
  }

  /**
   * Obtenir les préférences d'alertes
   */
  getPreferences(): NotificationPreferences {
    return getNotificationPreferences();
  }

  /**
   * Sauvegarder les préférences d'alertes
   */
  savePreferences(prefs: Partial<NotificationPreferences>): NotificationPreferences {
    return saveNotificationPreferences(prefs);
  }

  /**
   * Forcer une vérification des alertes
   */
  async forceCheck(restaurantId: string): Promise<Alert[]> {
    return this.checkStockAlerts(restaurantId);
  }

  /**
   * Arrêter le service
   */
  stop(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }
}

// Export d'une instance unique
export const alertService = new AlertService();
